import React, { useCallback, useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
// import style from './index.module.scss';
import cx from 'classnames';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import user from 'stores/user';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { GradientBorder } from 'theme/commonStyles';

const StyledButton = styled.button`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    outline: none;
    cursor: pointer;
    color: #fff;
    background: transparent;

    border-radius: 12px;
    transition: filter 0.3s ease-in-out;

    &:not(:disabled):hover,
    &:not(:disabled):focus,
    &:not(:disabled):active {
        filter: brightness(0.85);
    }
    &.xs {
        border-radius: 8px;
        font-size: 10px;
        width: fit-content;
        padding: 8px 10px;
    }
    &.small {
        height: 40px;
        border-radius: 8px;
        font-size: 14px;
        width: fit-content;
        font-weight: 400;
        padding: 0 20px;
    }

    &.middle {
        padding: 15px;
        font-size: 14px;
        border-radius: 8px;
        height: 56px;
        min-width: 180px;
    }

    &.normal {
        padding: 15px 24px;
        font-size: 14px;
        height: 64px;
        border-radius: 12px;
        box-sizing: border-box;
        font-weight: 500;
        min-width: 220px;

        ${({ theme }) => theme.mediaWidth.upToSmall`
        height 48px;
        font-size: 14px;
        padding: 10px 20px;
        min-width: unset;
        `};
    }

    &.primary {
        background: ${({ theme }) => theme.c.primary02};
        /* box-shadow: 2px 10px 10px 0px rgba(219, 154, 50, 0.3); */
        color: #fff;
    }
    &.secondary {
        background: ${({ theme }) => theme.c.primary01};
    }

    &.transparent {
        background: transparent;
        color: #fff;

        &:hover,
        &:focus,
        &:active {
            color: #fff;
        }
    }

    &.outline {
        background: transparent;
        border: 1px solid #dca45c;
        color: #dca45c;
    }
    &.outlineWhite {
        background: transparent;

        border: 1px solid #fff;
        color: #fff;
    }

    &.primaryWhite {
        background: #fff;
    }

    &.primaryGray {
        background: #e0e2eb;
    }
    &.gradient {
        background: ${({ theme }) => theme.c.linearGradientBg};
        color: #fff;
    }
    &.outlineGradient {
        ${GradientBorder};

        &:not(:disabled):hover,
        &:not(:disabled):focus,
        &:not(:disabled):active {
            filter: brightness(0.75);
        }
    }

    &.disabled {
        /* opacity: 0.6; */
        border-image: none;
        box-shadow: none;
        cursor: not-allowed;
        filter: brightness(0.6);

        &:hover,
        &:focus,
        &:active {
            border-image: none;
            box-shadow: none;
            cursor: not-allowed;
        }
    }

    &.loading {
        color: transparent !important;
    }

    .loadingIcon {
        position: absolute;
        // top: 50%;
        // left: 50%;
        // margin-top: -50%;
        // margin-left: -50%;
        color: #fff;
    }
`;
export interface Props {
    size?: 'middle' | 'normal' | 'small' | 'xs' | 'unset';
    type?:
        | 'primary'
        | 'transparent'
        | 'outline'
        | 'outlineWhite'
        | 'primaryWhite'
        | 'primaryGray'
        | 'gradient'
        | 'outlineGradient'
        | 'unstyled'
        | 'secondary';
    disabled?: boolean;
    children?: React.ReactNode;
    className?: string;
    showLoading?: boolean;
    loadingProps?: {
        classname?: string;
        size?: number;
        loading?: boolean;
    };
    style?: React.CSSProperties;
    auth?: boolean;
    onClick?: ((event?: React.SyntheticEvent) => void) | ((event?: React.SyntheticEvent) => Promise<any>);
    loading?: boolean;
}

function Button(props: Props) {
    const { openConnectModal } = useConnectModal();
    const {
        size = 'normal',
        type = 'primary',
        disabled,
        children,
        className,
        onClick,
        showLoading = true,
        loadingProps = {
            classname: '',
            size: 24,
        },
        auth,
        loading: propsLoading,
        ...rest
    } = props;
    const [loading, setLoading] = useState(propsLoading ?? false);
    const loadingRef = useRef(loading);
    const mountedRef = useRef(true);

    loadingRef.current = loading;

    const handleLoading = useCallback(
        (value: any) => {
            if (mountedRef.current) {
                setLoading(value);
            }
        },
        [setLoading],
    );

    const handleClick = useCallback(
        (event: any) => {
            if (loadingRef.current || disabled || !onClick) {
                return;
            }

            const promise = onClick(event);
            if (promise != null && typeof (promise as Promise<any>).then === 'function') {
                handleLoading(true);
                (promise as Promise<any>)
                    .then((result: any) => {
                        handleLoading(false);
                        return result;
                    })
                    .catch((e) => {
                        handleLoading(false);
                    });
            }
        },
        [loadingRef, mountedRef, disabled, handleLoading, onClick],
    );

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (propsLoading !== undefined) {
            setLoading(propsLoading);
        }
    }, [propsLoading]);

    const classes = [
        size,
        type,
        auth && !user.connected ? '' : disabled && 'disabled',
        loading && showLoading && 'loading',
        className,
    ];

    return auth && !user.connected ? (
        <StyledButton className={classes.join(' ')} onClick={openConnectModal} {...rest}>
            Connect Wallet
        </StyledButton>
    ) : (
        <StyledButton className={classes.join(' ')} onClick={handleClick} disabled={disabled} {...rest}>
            {children}
            {showLoading && loading && (
                <CircularProgress size={loadingProps.size} className={cx('loadingIcon', loadingProps.classname)} />
            )}
        </StyledButton>
    );
}
export default observer(Button);
